import React, { PropsWithChildren, ReactNode } from 'react';
import { SharedIcon } from '../../SharedIcon/SharedIcon';
import { useCollapse } from '../../../hooks/useCollapse';
import classNames from 'classnames';

interface CollapsibleCardProps {
  sectionKey: string;
  headerLeft: ReactNode;
  headerRight?: ReactNode;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
  onCollapse?: () => void;
  onStateChange?: (isExpanded: boolean) => void;
  defaultCollapsed?: boolean;
  arrowPosition?: 'left' | 'right';
  /** When false the card stays open, and the header is no longer a control. */
  collapsible?: boolean;
  dataTestId?: string;
}

export const CollapsibleCard = ({
  sectionKey,
  headerLeft,
  headerRight,
  className,
  headerClassName,
  contentClassName,
  onCollapse,
  onStateChange,
  defaultCollapsed,
  arrowPosition = 'right',
  collapsible = true,
  dataTestId,
  children,
}: PropsWithChildren<CollapsibleCardProps>) => {
  const contentId = React.useId();
  const collapse = useCollapse(sectionKey, defaultCollapsed);
  const { onToggleCollapse, contentRef } = collapse;
  // An always-open card has to report itself as expanded, both to `onStateChange`
  // consumers and to the `collapsed` header class the stylesheet keys off.
  const isExpanded = collapsible ? collapse.isExpanded : true;
  const show = collapsible ? collapse.show : true;
  const isTransitioning = collapsible ? collapse.isTransitioning : false;

  // Keep a stable ref so the effect only re-runs when `show` changes,
  // not when the caller passes a new inline callback reference.
  const onStateChangeRef = React.useRef(onStateChange);
  React.useEffect(() => {
    onStateChangeRef.current = onStateChange;
  });

  // Notify parent of state changes
  React.useEffect(() => {
    onStateChangeRef.current?.(show);
  }, [show]);

  const arrow = (
    <span className={!show ? 'collapse-arrow' : 'collapse-arrow collapsed'}>
      <SharedIcon iconName="keyboard_arrow_right" size="sm" />
    </span>
  );

  const handleToggle = () => {
    onToggleCollapse();
    if (show && onCollapse) {
      onCollapse();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleToggle();
  };

  const toggleProps = collapsible
    ? {
        onClick: handleToggle,
        onKeyDown: handleKeyDown,
        role: 'button',
        tabIndex: 0,
        'aria-expanded': isExpanded,
        'aria-controls': contentId,
      }
    : {};

  return (
    <div className={className} data-testid={dataTestId}>
      <div
        className={classNames(
          'fs-6 d-flex justify-content-between align-items-center header',
          headerClassName,
          { collapsed: !show }
        )}
        {...toggleProps}
      >
        <div className="d-flex align-items-center gap-2">
          {arrowPosition === 'left' && collapsible && arrow}
          {headerLeft}
        </div>
        <div className="d-flex align-items-center gap-2">
          {headerRight}
          {arrowPosition === 'right' && collapsible && arrow}
        </div>
      </div>
      {collapsible ? (
        <div
          id={contentId}
          ref={contentRef}
          className={classNames(isTransitioning ? 'collapsing' : 'collapse', {
            show: show,
          })}
          aria-hidden={!isExpanded}
        >
          <div className={contentClassName}>{children}</div>
        </div>
      ) : (
        <div id={contentId} className={contentClassName}>
          {children}
        </div>
      )}
    </div>
  );
};
